/* global THREE */
import ElasticNumber from '@/utils/ElasticNumber'
import ElasticVector3 from '@/utils/ElasticVector3'
import anime from 'animejs'
import 'three/SubdivisionModifier'
import 'three/SimplifyModifier'
import rand from 'random-seed'

export default class HopeConceptStone extends THREE.Mesh {
  //
  constructor (config, material, isCategory = false, bufferGeometry = true) {
    // build random seed
    let seed = `${config.id} ${config.color} ${config.label}`
    if (config.concepts) {
      config.concepts.forEach(concept => {
        seed += concept.id
      })
    }
    let randomGenerator = rand.create()
    randomGenerator.seed(seed)

    let vTotal = new THREE.Vector3()
    let vNumber = 0
    // let debug = false
    let geometry = new THREE.IcosahedronGeometry(1, 0)
    let categoryRotateZ
    if (!isCategory) {
      geometry.rotateX(randomGenerator.floatBetween(0, Math.PI))
      geometry.rotateY(randomGenerator.floatBetween(0, Math.PI))
      geometry.rotateZ(randomGenerator.floatBetween(0, Math.PI))
    } else {
      categoryRotateZ = randomGenerator.floatBetween(0, (Math.PI / 3))
      categoryRotateZ *= randomGenerator.random() > 0.5 ? 1 : -1
      geometry.scale(1 + (config.concepts.length / 5), 1, 1)
      geometry.rotateZ(categoryRotateZ)
    }

    geometry.vertices.forEach((vertex) => {
      if (!isCategory) {
        vertex.multiplyScalar(randomGenerator.floatBetween(0.3, 1))
      } else {
        vertex.multiplyScalar(randomGenerator.floatBetween(0.4, 1))
      }
      vTotal.add(vertex)
      vNumber++
    })

    vTotal.divideScalar(vNumber)
    geometry.translate(-vTotal.x, -vTotal.y, -vTotal.z)

    let sub = new THREE.SubdivisionModifier(2)
    sub.modify(geometry)

    let simpl = new THREE.SimplifyModifier()
    let collisionGeometry = simpl.modify(geometry, 50)
    // let collisionGeometry = geometry.clone() // simpl.modify(geometry, 50)
    collisionGeometry.computeVertexNormals()
    collisionGeometry.computeFaceNormals()
    collisionGeometry.computeBoundingSphere()

    super(bufferGeometry ? new THREE.BufferGeometry().fromGeometry(geometry) : geometry, material)
    this.categoryRotateZ = categoryRotateZ
    // let sc = 0.3 + (config.concepts.length / 4)
    this.scale.set(0.001, 0.001, 0.001)
    this.elasticPosition = new ElasticVector3(this.position)
    this.elasticPosition.speed = 20
    this.com = vTotal
    // this.castShadow = true
    this.collisionGeometry = collisionGeometry
    this.collisionMesh = new THREE.Mesh(new THREE.BufferGeometry().fromGeometry(this.collisionGeometry), new THREE.MeshBasicMaterial({
      color: 0x000000,
      wireframe: true,
      side: THREE.DoubleSide
    }))
    this.add(this.collisionMesh)

    this.rayCaster = new THREE.Raycaster()
    this.collisionArrowLength = 0.5

    this.redcolor = new THREE.Color(0xff0000)
    this.greenColor = new THREE.Color(0x00ff00)
    this.collisionOrigin = new THREE.Vector3()
    this.collisionDirection = new THREE.Vector3()
    this.collisionLOD = []
    // LOD 0: single ray
    this.collisionLOD[0] = [{
      ray: new THREE.Ray(new THREE.Vector3(), new THREE.Vector3(0, -1, 0)), // vertical
      arrow: new THREE.ArrowHelper(new THREE.Vector3(0, -1, 0), new THREE.Vector3(), this.collisionArrowLength, 0x00ff00),
      r: 0
    }]
    // this.collisionLOD[1] = this.createRadialCollisionRaysFromCenter(2, 0.2, 0.3, 3)
    // this.collisionLOD[2] = this.createParallelCollisionRaysForLowerVertices(10)

    this.verticalAcceleration = 0
    this.oscillationSize = THREE.Math.randFloat(0.2, 0.5)
    this.oscillationSpeed = THREE.Math.randFloatSpread(0.05)
    this.elasticOscillationSpeed = new ElasticNumber(0)
    this.oscillationTick = 0
    this.rotationRate = 0
    this.elasticRotationRate = new ElasticNumber(0)

    this.__visible = false
    this.spacing = 0
    this.rayScanTick = 0
    this.rayScanSpeed = 0.4
    this.debug = false
    this.rotate = true
    this.oscillate = false
    this.activeCollisionLOD = 0
    // this.rotationDirection = (Math.round(Math.random()) * 2 - 1) // random 1 or -1
    this.config = config
    randomGenerator.done()
  }

  get isConceptStone () {
    return this._config ? this._config.concepts !== undefined : false
  }

  get config () {
    return this._config
  }

  set config (value) {
    this._config = value
    this.rotationDirection = value.trend !== undefined ? value.trend : 1

    if (this.isConceptStone) {
      this.material.map.draw(value.color, value.concepts.length)
    } else {
      this.rotationRate = (value.activity * 0.1) * this.rotationDirection
      this.elasticRotationRate.target = this.rotationRate
    }
    if (this.__visible) {
      this.setScale()
    }
  }

  setVisible (value = true, instantaneous = false) {
    //
    if (value && !this.__visible) {
      this.visible = true
      this.setScale(true)
    }

    if (!value && this.__visible) {
      this.cancelScaleAnimation()
      this.scaleAnimation = anime({
        targets: this.scale,
        x: 0.001,
        y: 0.001,
        z: 0.001,
        easing: 'easeOutElastic',
        complete: (anim) => {
          this.visible = false
          this.scaleAnimation = null
        }
      })
    }
    this.__visible = value
  }

  setScale (fromZero = false, animate = true) {
    this.cancelScaleAnimation()
    let sc = 0.3
    if (this._config.concepts) {
      // this is a "category" stone
      sc = 0.3 + (this._config.concepts.length / 10)
    } else {
      // this is a concept stone
      sc += 0.2 * this._config.importance
    }
    if (fromZero) {
      this.scale.set(0.001, 0.001, 0.001)
    }

    if (animate) {
      this.animateScale(sc)
    } else {
      this.scale.set(sc, sc, sc)
    }
  }

  // getCategoryScale (multiplier) {
  //   // rotate the multiplier
  //   // let v = new THREE.Vector3(multiplier, 0, 0)
  //   // v.applyAxisAngle(new THREE.Vector3(0, 0, 1), this.categoryRotateZ)

  //   let m = new THREE.Matrix4()
  //   m.makeRotationX(this.categoryRotateZ)

  //   let m2 = new THREE.Matrix4()
  //   m2.makeScale(0.3 + multiplier, 0.3, 0.3)

  //   m.premultiply(m2)

  //   // m.makeShear(v.x, v.y, 0)
  //   // m.scale(new THREE.Vector3(0.3, 0.3, 0.3))
  //   let scale = new THREE.Vector3()
  //   let quaternion = new THREE.Quaternion()
  //   m.decompose(new THREE.Vector3(), quaternion, scale)

  //   scale.applyQuaternion(quaternion)
  //   // let scX = 0.3 + 0.8 * Math.abs(scale.x)
  //   // let scY = 0.3 + 0.8 * Math.abs(scale.y)
  //   let scX = Math.abs(scale.x)
  //   let scY = Math.abs(scale.y)
  //   return {scX, scY}
  // }

  cancelScaleAnimation () {
    if (this.scaleAnimation) {
      this.scaleAnimation.pause()
      anime.remove(this.scale)
    }
  }

  animateScale (sc) {
    this.scaleAnimation = anime({
      targets: this.scale,
      x: sc,
      y: sc,
      z: sc,
      easing: 'easeOutElastic',
      complete: (anim) => {
        this.scaleAnimation = null
      }
    })
  }

  get activeCollisionLOD () {
    return this._activeCollisionLOD
  }

  set activeCollisionLOD (value) {
    this._activeCollisionLOD = value

    // create collision LOD rays in demand
    if ((value === 1 || value === 2) && !this.collisionLOD[1]) {
      this.collisionLOD[1] = this.createRadialCollisionRaysFromCenter(2, 0.2, 0.3, 3)
    }
    if (value === 2 && !this.collisionLOD[2]) {
      this.collisionLOD[2] = this.createParallelCollisionRaysForLowerVertices(10)
    }

    this.collisionRays = []
    for (let i = 0; i <= value; i++) {
      this.collisionRays = this.collisionRays.concat(this.collisionLOD[i])
    }

    while (this.collisionMesh.children.length > 0) {
      this.collisionMesh.remove(this.collisionMesh.children[0])
    }

    this.precomputeCollisionRaysOwnIntersection()

    if (this.debug) {
      this.collisionRays.forEach((object) => {
        this.collisionMesh.add(object.arrow)
      })
    }
  }
  get debug () {
    return this._debug
  }

  set debug (value) {
    this._debug = value
    this.collisionMesh.visible = value
    this.material.transparent = value
    this.material.opacity = value ? 0.7 : 1
  }

  get oscillate () {
    return this._oscillate
  }

  set oscillate (value) {
    if (!this.isConceptStone) {
      this.elasticOscillationSpeed.target = value ? this.oscillationSpeed : 0
      this._oscillate = value
    } else {
      this._oscillate = false
    }
  }

  get rotate () {
    return this._rotate
  }

  set rotate (value) {
    if (!this.isConceptStone) {
      this.elasticRotationRate.target = value ? this.rotationRate : 0
      this._rotate = value
    } else {
      this._rotate = false
    }
  }

  update (parent, delta, skipPhysics = false) {
    // check if we need to update physics
    if (!skipPhysics) {
      // check if we have a parent
      if (parent) {
        // this.rayScan()
        let compenetration = this.singleCollisionTest(parent)
        if (compenetration <= 0) {
          this.elasticPosition.y -= this.verticalAcceleration
          this.verticalAcceleration += 0.0004
        } else {
          this.verticalAcceleration = 0
          // this.elasticPosition.y = this.elasticPosition.value.y = this.elasticPosition.value.y + compenetration
          this.elasticPosition.y = this.elasticPosition.value.y + compenetration
        }
        // falling stones fix
        if (this.elasticPosition.y <= parent.position.y + 0.1) {
          if (parent.collisionMesh) {
            // we are colliding with another stone
            this.verticalAcceleration = -0.02
            this.elasticPosition.y = parent.position.y + 0.2
          } else {
            // we are colliding with ground
            // for some reason if we don't do this
            // stones will keep bouncing off the ground
            this.verticalAcceleration = -0.01
            this.elasticPosition.y = parent.position.y
          }
        }
      } else if (parent === null || parent === undefined) {
        // no parent = bottom stone
        if (!this.groundCheck()) {
          // fall to ground
          this.elasticPosition.y -= this.verticalAcceleration
          this.verticalAcceleration += 0.0004
        } else {
          // stay still
          this.verticalAcceleration = 0
          this.elasticPosition.y = 0
        }
      }
    }

    this.elasticRotationRate.update(delta)
    this.rotation.y += this.elasticRotationRate.value

    this.elasticOscillationSpeed.update(delta)
    this.oscillationTick += this.elasticOscillationSpeed.value
    this.rotation.z = Math.sin(this.oscillationTick) * this.oscillationSize

    this.elasticPosition.update(delta)
  }

  groundCheck () {
    return this.position.y <= 0
  }

  rayScan () {
    // this.rayScanTick += this.rayScanSpeed
    let axis = new THREE.Vector3(0, -1, 0)

    for (let i = 0; i < this.collisionRays.length; i++) {
      let object = this.collisionRays[i]
      object.ray.direction.applyAxisAngle(axis, THREE.Math.randFloat(0.1, 2))
      // object.ray.direction.applyAxisAngle(axis, this.rayScanSpeed)
      object.arrow.setDirection(object.ray.direction)
    }
  }

  precomputeCollisionRaysOwnIntersection () {
    this.updateMatrixWorld(true)
    this.collisionMesh.updateMatrixWorld(true)

    this.collisionRays.forEach((object) => {
      this.collisionOrigin.copy(object.ray.origin)
      this.collisionOrigin.applyMatrix4(this.matrixWorld)
      this.collisionDirection.copy(object.ray.direction)
      this.collisionDirection.transformDirection(this.matrixWorld)
      this.rayCaster.set(this.collisionOrigin, this.collisionDirection)
      let selfIntersections = []
      this.collisionMesh.material.side = THREE.BackSide // test backside of self
      this.collisionMesh.raycast(this.rayCaster, selfIntersections)

      if (selfIntersections.length > 0) {
        object.selfIntersection = -this.collisionMesh.worldToLocal(selfIntersections[0].point).y
      }
    })

    this.collisionRays.sort((a, b) => {
      return a.selfIntersection > b.selfIntersection ? -1 : 1
    })
  }
  // palallel collision rays for each
  createParallelCollisionRaysForLowerVertices (num = 8) {
    let ret = []
    let vert = this.collisionGeometry.vertices.concat()
    vert.sort((a, b) => {
      return a.y < b.y ? -1 : 1
    })
    // select lower vertices
    vert = vert.slice(0, num)
    vert.forEach((v) => {
      let v2 = v.clone()
      v2.y = 0
      ret.push({
        ray: new THREE.Ray(v2, new THREE.Vector3(0, -1, 0).normalize()),
        arrow: new THREE.ArrowHelper(new THREE.Vector3(0, -1, 0).normalize(), v2, this.collisionArrowLength, 0x00ff00),
        r: 0
      })
    })
    return ret
  }

  createRadialCollisionRaysForLowerVertices (num = 8) {
    let ret = []
    let vert = this.collisionGeometry.vertices.concat()
    vert.sort((a, b) => {
      return a.y < b.y ? -1 : 1
    })
    // select lower vertices
    vert = vert.slice(0, num)
    vert.forEach((v) => {
      let v2 = v.clone()
      v2.normalize()
      ret.push({
        ray: new THREE.Ray(new THREE.Vector3(), v2),
        arrow: new THREE.ArrowHelper(v2, new THREE.Vector3(), this.collisionArrowLength, 0x00ff00),
        r: 0
      })
    })
    return ret
  }

  createRadialCollisionRaysFromCenter (numRadius = 2, radiusStart = 0.5, radiusIncrement = 1, numRaysPerRadius = 3) {
    let ret = []

    for (let i = 0; i < numRadius; i++) {
      let radius = radiusStart + (i * radiusIncrement)
      for (let k = 0; k < numRaysPerRadius; k++) {
        let offset = i % 2 === 0 ? 0 : Math.PI / 4
        let r = k * ((Math.PI * 2) / numRaysPerRadius) + offset
        let x = radius * Math.cos(r)
        let y = radius * Math.sin(r)
        ret.push({
          ray: new THREE.Ray(new THREE.Vector3(), new THREE.Vector3(x, -1, y).normalize()),
          arrow: new THREE.ArrowHelper(new THREE.Vector3(x, -1, y).normalize(), new THREE.Vector3(), this.collisionArrowLength, 0x00ff00),
          r: radius
        })
      }
    }

    return ret
  }

  singleCollisionTest (parent, stopAtComp = 2) {
    if (this.debug) {
      this.collisionRays.forEach((obj) => {
        obj.arrow.setColor(this.greenColor)
      })
    }

    let compenetration = 0
    let numConfirmedCompenetrations = 0

    for (let i = 0; i < this.collisionRays.length; i++) {
      let object = this.collisionRays[i]

      this.collisionOrigin.copy(object.ray.origin)
      this.collisionOrigin.applyMatrix4(this.matrixWorld)
      this.collisionDirection.copy(object.ray.direction)
      this.collisionDirection.transformDirection(this.matrixWorld)
      this.rayCaster.set(this.collisionOrigin, this.collisionDirection)

      let parentIntersections = []
      let collisionMesh = parent.collisionMesh || parent
      let parentMaterialSide = collisionMesh.material.side
      collisionMesh.material.side = THREE.FrontSide // test front side of parent
      collisionMesh.raycast(this.rayCaster, parentIntersections)
      collisionMesh.material.side = parentMaterialSide // reset material side

      if (parentIntersections.length > 0) {
        let intersectionPoint = this.parent.worldToLocal(parentIntersections[0].point.clone())
        let int = Math.abs(intersectionPoint.y - this.position.y)
        // console.log(int)
        let cmp = ((object.selfIntersection * this.scale.y) - int) + this.spacing
        compenetration = Math.max(compenetration, cmp)
        if (cmp > 0) {
          numConfirmedCompenetrations++
          if (this.debug) {
            object.arrow.setColor(this.redcolor)
          }
        }
      }
      if (numConfirmedCompenetrations === stopAtComp) {
        break
      }
    }
    return compenetration
  }

  doubleCollisionTest (parent, stopAtComp = 2) {
    if (this.debug) {
      this.collisionRays.forEach((obj) => {
        obj.arrow.setColor(this.greenColor)
      })
    }

    let compenetration = 0
    let numConfirmedCompenetrations = 0

    for (let i = 0; i < this.collisionRays.length; i++) {
      let object = this.collisionRays[i]

      this.collisionOrigin.copy(object.ray.origin)
      this.collisionOrigin.applyMatrix4(this.matrixWorld)
      this.collisionDirection.copy(object.ray.direction)
      // direction.transformDirection(this.matrixWorld)
      this.rayCaster.set(this.collisionOrigin, this.collisionDirection)

      let parentIntersections = []
      let selfIntersections = []
      parent.collisionMesh.material.side = THREE.FrontSide // test front side of parent
      parent.collisionMesh.raycast(this.rayCaster, parentIntersections)

      if (parentIntersections.length > 0) {
        // if intersects with parent
        this.collisionMesh.material.side = THREE.BackSide // test backside of self
        this.collisionMesh.raycast(this.rayCaster, selfIntersections)

        if (selfIntersections.length > 0) {
          let cmp = (selfIntersections[0].distance - parentIntersections[0].distance)
          compenetration = Math.max(compenetration, cmp)
          if (cmp > 0) {
            if (this.debug) {
              object.arrow.setColor(this.redcolor)
            }
            numConfirmedCompenetrations++
          }
        }
      }
      if (numConfirmedCompenetrations === stopAtComp) {
        break
      }
    }

    return compenetration
  }

  addWheelShadowPlane () {
    let geom = new THREE.PlaneBufferGeometry(2, 2)
    let material = new THREE.MeshBasicMaterial({
      transparent: true,
      map: new THREE.TextureLoader().load('/static/img/shadow1.png'),
      side: THREE.DoubleSide,
      depthWrite: false
    })
    let plane = new THREE.Mesh(geom, material)
    plane.position.copy(this.collisionGeometry.boundingSphere.center)
    plane.position.z -= this.collisionGeometry.boundingSphere.radius
    this.add(plane)
  }

  destroy () {
    this.geometry.dispose()
    this.material.map.dispose()
    this.material.dispose()

    this.collisionMesh.traverse(child => {
      child.material.dispose()
      if (child.material.map) child.material.map.dispose()
      child.geometry.dispose()
    })

    while (this.collisionMesh.children.length > 0) {
      this.collisionMesh.remove(this.collisionMesh.children[0])
    }

    this.collisionMesh.material.dispose()
    this.collisionMesh.geometry.dispose()
    this.remove(this.collisionMesh)
  }
}



// WEBPACK FOOTER //
// ./src/atoms/HopeConceptStone.js